import { useEffect, useRef, useState } from "react";
import DriverCashLedgerService, {
  DriverCashLedger,
  LedgerStatus,
  LedgerTransactionType,
} from "../services/driver-cash-ledger-service";
import { isSuccess } from "../../../models/response/base-response";
import EmptyState from "../../../components/custom/empty-state";
import { formatCurrency } from "../../../utils/helpers";
import { formatServerDateTime } from "../../order-list/utils/datetime";
import { TABLE_CLASS, TD, TD_NUM, TH, THEAD_CLASS } from "../../sales-output-report/components/service-table";

const PAGE_SIZE = 20;

interface Props {
  driverId: number;
  driverName: string;
  /** null = nhóm "chưa xác định chi nhánh" */
  branchId: number | null;
  branchName: string;
  /** YYYY-MM-DD */
  startDate: string;
  endDate: string;
  onClose: () => void;
}

// API danh sách giao dịch nhận dd/mm/yyyy.
const toDMY = (ymd: string) => {
  const [y, m, d] = (ymd || "").split("-");
  return d && m && y ? `${d}/${m}/${y}` : "";
};

// Accounting trả giờ dạng UTC ISO "…Z" → hiện theo giờ VN cố định.
const formatDateTime = (s: string) => formatServerDateTime(s);

const TYPE_LABEL: Record<number, string> = {
  [LedgerTransactionType.THU_TIEN_KHACH]: "Thu tiền khách",
  [LedgerTransactionType.NOP_TIEN]: "Nộp tiền",
  [LedgerTransactionType.CHI_KHAC]: "Chi khác",
};

/**
 * Giao dịch sổ quỹ của 1 nhân viên tại 1 chi nhánh trong kỳ (dòng của tab "Theo nhân viên"). Dùng lại API danh
 * sách giao dịch, lọc thêm `branch_id` (0 = chưa xác định). Phân trang phía server.
 */
const CashLedgerTransactionsModal: React.FC<Props> = ({ driverId, driverName, branchId, branchName, startDate, endDate, onClose }) => {
  const [rows, setRows] = useState<DriverCashLedger[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const { fetch } = DriverCashLedgerService.list();
  const fetchRef = useRef(fetch);
  useEffect(() => { fetchRef.current = fetch; }, [fetch]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res: any = await fetchRef.current({
          driver_id: driverId,
          branch_id: branchId ?? 0,
          status: LedgerStatus.APPROVED, // khớp báo cáo: chỉ giao dịch đã ghi nhận
          from_date: toDMY(startDate),
          to_date: toDMY(endDate),
          page,
          limit: PAGE_SIZE,
        });
        if (!cancelled && res && isSuccess(res)) {
          setRows(res.data?.list ?? []);
          setTotal(Number(res.data?.total_record) || 0);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoaded(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [driverId, branchId, startDate, endDate, page]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-4" onClick={onClose}>
      <div
        className="flex max-h-[calc(100vh-2rem)] w-full max-w-5xl flex-col rounded-2xl bg-white shadow-xl border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-200 p-5">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Giao dịch sổ quỹ — {driverName}</h3>
            <p className="mt-0.5 text-sm text-slate-500">
              {branchName} · {toDMY(startDate)} → {toDMY(endDate)} · {total} giao dịch
            </p>
          </div>
          <button onClick={onClose} aria-label="Đóng" className="text-lg text-slate-500 hover:text-slate-700">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="overflow-auto rounded-lg border border-gray-200 bg-white">
            {loading && !loaded ? (
              <div className="py-16 text-center text-sm text-gray-400">Đang tải...</div>
            ) : rows.length === 0 ? (
              <EmptyState message="Không có giao dịch" description="Nhân viên không có giao dịch nào trong kỳ tại chi nhánh này." />
            ) : (
              <div className={loading ? "opacity-60 transition-opacity" : "transition-opacity"}>
                <table className={TABLE_CLASS}>
                  <thead className={THEAD_CLASS}>
                    <tr>
                      <th className={`${TH} w-14`}>STT</th>
                      <th className={TH}>Thời gian</th>
                      <th className={TH}>Loại</th>
                      <th className={`${TH} min-w-[220px]`}>Diễn giải</th>
                      <th className={TH}>Thu (đ)</th>
                      <th className={TH}>Nộp (đ)</th>
                      <th className={TH}>Tồn sau GD (đ)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const amount = Number(r.amount) || 0;
                      return (
                        <tr key={r.id} className="hover:bg-gray-50">
                          <td className={`${TD} text-center text-gray-400`}>{(page - 1) * PAGE_SIZE + i + 1}</td>
                          <td className={`${TD} whitespace-nowrap text-gray-600`}>{formatDateTime(r.created_at)}</td>
                          <td className={`${TD} whitespace-nowrap`}>{TYPE_LABEL[Number(r.transaction_type)] || "Khác"}</td>
                          <td className={TD}>
                            <div className="text-gray-900">{r.note || "—"}</div>
                            {r.customer_name && <div className="text-xs text-gray-400">{r.customer_name}</div>}
                          </td>
                          <td className={`${TD_NUM} font-semibold text-emerald-600`}>{amount > 0 ? formatCurrency(amount) : ""}</td>
                          <td className={`${TD_NUM} font-semibold text-red-500`}>{amount < 0 ? formatCurrency(-amount) : ""}</td>
                          {/* Tồn sau GD là số dư CHUNG của nhân viên (mọi chi nhánh) tại thời điểm đó. */}
                          <td className={`${TD_NUM} text-gray-700`}>{formatCurrency(r.balance_after)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <p className="mt-2 text-xs text-gray-400">
            "Tồn sau GD" là số dư chung của nhân viên (gồm mọi chi nhánh) tại thời điểm giao dịch.
          </p>
        </div>

        <div className="flex shrink-0 items-center justify-between border-t border-slate-200 p-4">
          <span className="text-sm text-gray-500">
            {total > 0 ? `Hiển thị ${(page - 1) * PAGE_SIZE + 1}-${Math.min(page * PAGE_SIZE, total)} / ${total}` : ""}
          </span>
          <div className="flex items-center gap-2">
            {total > PAGE_SIZE && (
              <>
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1 || loading}
                  className="px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30"
                >
                  ‹
                </button>
                <span className="text-sm text-gray-600">Trang {page}/{totalPages}</span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages || loading}
                  className="px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30"
                >
                  ›
                </button>
              </>
            )}
            <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-slate-50">Đóng</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashLedgerTransactionsModal;
